import React, { useState } from 'react';
import { useLazyGetSocialSharingQuery } from '../api/PostApi';
import { FormHookReturnType, FormValueType } from './useFormValues';
import isURL from 'validator/lib/isURL';
import { Post } from 'model';

/**
 * Hook that returns a onPaste handler for text areas that will attempt to parse the pasted URL
 * using the server side social sharing API that looks from open graph meta tags.
 *
 * If the URL is a valid image URL, it will insert the image markdown.
 *
 * If the URL is a valid URL, but doesn't get a good response from the API, it will just insert the URL with the correct markdown.
 */

export function usePasteURL<T extends Post>(setValues: FormHookReturnType<T>['setValues'], fieldName: keyof T) {
	const [loading, setLoading] = useState(false);
	const [trigger] = useLazyGetSocialSharingQuery();

	const onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		let url = '';
		if (e.clipboardData.types.indexOf('text/plain') > -1) {
			url = e.clipboardData.getData('text/plain');
			if (isURL(url)) {
				setLoading(true);
				const result = await trigger(url);
				setLoading(false);
				const sharing = result.data;
				if (sharing) {
					if (sharing.title && sharing.url) {
						setValues(values => {
							return {
								linkPreview: {
									url: sharing.url,
									title: sharing.title,
									image: sharing.image,
									description: sharing.description
								}
							} as FormValueType<T>;
						});
					}
					if (sharing.url === '') {
						// Handle links that really didn't have a good response from the API
						// by just inserting the url with the correct markdown
						setValues(values => {
							let insert = '';
							const oldValue = values?.[fieldName]?.value as string;
							if (
								url.endsWith('jpg') ||
								url.endsWith('jpeg') ||
								url.endsWith('webp') ||
								url.endsWith('png') ||
								url.endsWith('gif')
							) {
								insert = `
![${sharing.title ? sharing.title : 'A pasted image'}](${url})`;
							} else {
								insert = `
[${sharing.title ? sharing.title : url}](${url})`;
							}
							const value = oldValue?.replace(url, insert);
							if (value) {
								return {
									[fieldName]: value
								} as FormValueType<T>;
							}
							return { [fieldName]: oldValue } as FormValueType<T>;
						});
					}
				}
			}
		}
	};
	return { onPaste, loading };
}
